"use client";

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { fetchProfile, fetchCompany } from '@/utils/fetchProfile';
import { Profile } from '@/types/supabase';
import { showError } from '@/utils/toast';
import { resolveTenantByHost } from '@/utils/tenant';

interface DataProviderProps {
  children: React.ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const { user, setProfile, setCompany } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [tenant, setTenant] = useState<any>(null); // State to hold tenant data

  // Helper function to safely fetch profile with timeout
  const safeFetchProfile = async (userId: string, timeoutMs: number = 10000): Promise<Profile | null> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const profileData = await fetchProfile(userId);
      clearTimeout(timeoutId);
      return profileData;
    } catch (error: any) {
      clearTimeout(timeoutId);
      console.error('Safe fetchProfile error:', error);
      // Don't throw - return null to allow app to continue
      return null;
    }
  };

  // Helper function to safely fetch company with timeout
  const safeFetchCompany = async (companyId: string, timeoutMs: number = 5000): Promise<any> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const companyData = await fetchCompany(companyId);
      clearTimeout(timeoutId);
      return companyData;
    } catch (error: any) {
      clearTimeout(timeoutId);
      console.error('Safe fetchCompany error:', error);
      // Don't throw - return null
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      
      // Always try to resolve tenant first, regardless of user login status
      const resolvedTenant = await resolveTenantByHost();
      if (resolvedTenant) {
        setTenant(resolvedTenant);
        setCompany(resolvedTenant); // Update company context with resolved tenant
      } else {
        setTenant(null);
        setCompany(null);
      }

      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const profileData = await safeFetchProfile(user.id, 10000);
        if (profileData) {
          setProfile(profileData);
          // If profile has a company_id, ensure it matches the resolved tenant or fetch it
          if (profileData.company_id && (!resolvedTenant || resolvedTenant.id !== profileData.company_id)) {
            const companyData = await safeFetchCompany(profileData.company_id, 5000);
            setCompany(companyData);
          }
        } else {
          console.warn('DataProvider: No profile data found for user:', user.id);
          showError('Failed to load profile data.');
        }
      } catch (error: any) {
        console.error('DataProvider: Error fetching data:', error);
        showError(error.message || 'Failed to load data.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user, setProfile, setCompany]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="i-heroicons-arrow-path h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return children;
};

export default DataProvider;